<script lang="ts">
  import { Icon } from '$ui/primitives/Icon';

  let {
    value = $bindable<string | null>(null),
    options,
    placeholder,
    disabled = false,
    loading = false,
    required = false,
    size = 'md',
    label,
    helper,
    error,
    id = `select-${Math.random().toString(36).slice(2, 9)}`,
    onchange,
    class: className = ''
  }: {
    value?: string | null;
    options: Array<{ value: string; label: string }>;
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    required?: boolean;
    size?: 'sm' | 'md';
    label?: string;
    helper?: string;
    error?: string;
    id?: string;
    onchange?: (value: string | null) => void;
    class?: string;
  } = $props();

  const helperId = $derived(helper ? `${id}-helper` : undefined);
  const errorId = $derived(error ? `${id}-error` : undefined);
  const describedBy = $derived(errorId ?? helperId ?? undefined);
  const isEmpty = $derived(value === null || value === '');

  let selectEl: HTMLSelectElement | undefined = $state();

  // Clave estable del CONTENIDO de las options. Cuando llegan asíncronas,
  // el DOM <select> no re-aplica `value` aunque el prop cambie — este
  // effect fuerza la re-sincronización tras cada cambio de options o value.
  let optionsKey = $derived(options.map((o) => o.value).join('|'));

  $effect(() => {
    void optionsKey;
    void value;
    if (!selectEl) return;
    const target = value ?? '';
    if (selectEl.value !== target) selectEl.value = target;
  });

  function handleChange(e: Event) {
    const v = (e.currentTarget as HTMLSelectElement).value;
    value = v === '' ? null : v;
    onchange?.(value);
  }
</script>

<div class="field {className}" data-invalid={!!error || undefined} data-disabled={disabled || undefined}>
  {#if label}
    <label class="label" for={id}>{label}</label>
  {/if}
  <div class="wrap">
    {#if loading}
      <div
        class="skeleton"
        data-size={size}
        role="status"
        aria-label="Loading options"
      ></div>
    {:else}
      <select
        bind:this={selectEl}
        {id}
        {disabled}
        required={required || undefined}
        aria-required={required || undefined}
        data-size={size}
        data-empty={isEmpty || undefined}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        onchange={handleChange}
      >
        {#if placeholder}
          <option value="" disabled selected={isEmpty}>{placeholder}</option>
        {/if}
        {#each options as o (o.value)}
          <option value={o.value} selected={o.value === value}>{o.label}</option>
        {/each}
      </select>
      <span class="chevron" aria-hidden="true">
        <Icon name="chevron-down" size={16} />
      </span>
    {/if}
  </div>
  {#if error}
    <span id={errorId} class="error">{error}</span>
  {:else if helper}
    <span id={helperId} class="helper">{helper}</span>
  {/if}
</div>

<style>
  .field {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .label {
    font-size: 0.75rem;
    font-weight: var(--weight-semibold);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  .wrap {
    position: relative;
    display: flex;
    align-items: center;
  }

  select {
    width: 100%;
    font-family: var(--font-sans);
    color: var(--color-text-primary);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-sm);
    padding-right: var(--space-8);
    cursor: pointer;
    appearance: none;
    transition:
      border-color var(--dur-fast) var(--ease-out),
      background var(--dur-fast) var(--ease-out);
  }

  select[data-size='sm'] {
    height: 40px;
    padding-left: var(--space-3);
    font-size: var(--text-sm);
  }

  select[data-size='md'] {
    height: 44px;
    padding-left: var(--space-3);
    font-size: var(--text-body);
  }

  select[data-empty] {
    color: var(--color-text-muted);
  }

  select:hover:not(:disabled) {
    border-color: var(--color-text-faint);
  }

  select:focus-visible {
    border-color: var(--color-accent);
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: var(--focus-ring-offset);
  }

  select:disabled {
    background: var(--color-surface);
    color: var(--color-text-muted);
    cursor: not-allowed;
  }

  .field[data-invalid] select {
    border-color: var(--color-danger);
  }

  .chevron {
    position: absolute;
    right: var(--space-3);
    display: flex;
    align-items: center;
    color: var(--color-text-muted);
    pointer-events: none;
  }

  /* Skeleton state: ocupa el mismo espacio que el <select> real
     (F6 §3 Doherty threshold + F8 §7.3). */
  .skeleton {
    width: 100%;
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-sm);
    overflow: hidden;
    position: relative;
  }

  .skeleton[data-size='sm'] { height: 40px; }
  .skeleton[data-size='md'] { height: 44px; }

  .skeleton::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--color-surface-hover) 50%,
      transparent 100%
    );
    animation: skeleton-shimmer 1.4s ease-in-out infinite;
  }

  @keyframes skeleton-shimmer {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  @media (prefers-reduced-motion: reduce) {
    .skeleton::after { animation: none; opacity: 0.4; }
  }

  .helper {
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .error {
    font-size: var(--text-sm);
    color: var(--color-danger);
  }
</style>